import { ScrollArea } from "@/components/ui/scroll-area";
import AIResponse, {
    MoreDataCallbackResult,
    type ReasoningItem,
} from "@/domain/entities/vistassistant/AIResponse";
import ConversationMarker, {
    type ThreadItem,
} from "@/domain/entities/vistassistant/ConversationMarker";
import type { PendingTicketFormData } from "@/domain/entities/vistassistant/TicketTypes";
import { TicketType } from "@/domain/entities/vistassistant/TicketTypes";
import UserRequest from "@/domain/entities/vistassistant/UserRequest";
import { cn } from "@/lib/utils";
import type { AnswerFeedback } from "@cs_teamwaydev/mailvista-sdk_react";
import {
    memo,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import AssistantLogo from "./AssistantLogo";
import CompactionMarker from "./CompactionMarker";
import MessageItem from "./MessageItem";
import { questionAnchorId } from "./QuestionOutline";
import ThinkingIndicator from "./ThinkingIndicator";

interface MessageListProps {
    className?: string;
    handleMoreDataCallBack: (
        callback: () => Promise<MoreDataCallbackResult>,
    ) => void;
    messages: ThreadItem[];
    isLoadingMoreData: boolean;
    onBulkAction: (message: AIResponse) => void | Promise<void>;
    actionDoneRequestIds: Set<string>;
    /** "Retry" on the error bubble of a failed last turn (F-1.17). */
    onRetry?: () => void;
    /** Step-based contract: the thumb given under each answer, by turn. */
    answerFeedback?: Record<string, AnswerFeedback>;
    /** Step-based contract: thumbs under the answers (absent: none). */
    onAnswerFeedback?: (turnId: string, feedback: AnswerFeedback) => void;
    /** A turn is running: « L'assistant réfléchit » at the end of the thread. */
    isThinking?: boolean;
    /** Reasoning of the turn still running (step-based contract), shown with it. */
    pendingReasoning?: ReasoningItem[];
    onSubmitTicket?: (
        data: PendingTicketFormData,
        ticketType: TicketType,
    ) => Promise<void>;
    onConfirmCreateFromSearch?: (
        pendingData: PendingTicketFormData,
        ticketType: TicketType,
    ) => void;
}

const NEAR_BOTTOM_THRESHOLD = 150;

const getMessageKey = (message: ThreadItem): string => {
    if (message instanceof UserRequest) return `user-${message.id}`;
    if (message instanceof ConversationMarker)
        return `marker-${message.at.getTime()}`;

    return `ai-${message.userRequestId}`;
};

// messages.length can't drive the scroll: trimMessages caps the thread, so appending
// a bubble to a full thread leaves the count unchanged. Track the last bubble's identity.
const getLastMessageKey = (msgs: ThreadItem[]) =>
    msgs.length ? getMessageKey(msgs[msgs.length - 1]) : null;

const hasAnalysis = (response: AIResponse) =>
    response.isAnalysisPending ||
    !!response.deepAnalysis.result ||
    !!response.deepAnalysis.error;

/**
 * One answer may come in several blocks (mails, events…). Its analyses go after the last block:
 * index of that block → the blocks' analyses, in their order.
 */
const analysesAtAnswerEnd = (
    messages: ThreadItem[],
): Map<number, AIResponse[]> => {
    const atEnd = new Map<number, AIResponse[]>();
    let answer: AIResponse[] = [];
    messages.forEach((message, index) => {
        if (!(message instanceof AIResponse)) {
            answer = [];

            return;
        }
        answer.push(message);
        if (!(messages[index + 1] instanceof AIResponse)) {
            const analyses = answer.filter(hasAnalysis);
            if (analyses.length) atEnd.set(index, analyses);
        }
    });

    return atEnd;
};

const nextQuestionAfter = (messages: ThreadItem[], index: number) => {
    const next = messages
        .slice(index + 1)
        .find((message) => message instanceof UserRequest);

    return next instanceof UserRequest ? next.content : undefined;
};

const AssistantTurn = ({
    withLogo,
    children,
}: {
    withLogo: boolean;
    children: ReactNode;
}) => (
    <div className="flex w-full gap-3">
        {withLogo ? (
            <AssistantLogo size="md" />
        ) : (
            <span aria-hidden className="w-10 flex-none" />
        )}
        <div className="min-w-0 flex-1">{children}</div>
    </div>
);

const MessageList = ({
    className,
    handleMoreDataCallBack,
    messages,
    isLoadingMoreData,
    onBulkAction,
    actionDoneRequestIds,
    onRetry,
    answerFeedback,
    onAnswerFeedback,
    onSubmitTicket,
    onConfirmCreateFromSearch,
    isThinking = false,
    pendingReasoning,
}: MessageListProps) => {
    const [lastMessageKey, setLastMessageKey] = useState(() =>
        getLastMessageKey(messages),
    );
    const scrollViewportRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
        const scrollViewport = scrollViewportRef.current;
        if (scrollViewport) {
            scrollViewport.scrollTo({
                top: scrollViewport.scrollHeight,
                behavior,
            });
        }
    };

    const isNearBottom = () => {
        const scrollViewport = scrollViewportRef.current;
        if (!scrollViewport) return true;

        return (
            scrollViewport.scrollHeight -
                scrollViewport.scrollTop -
                scrollViewport.clientHeight <
            NEAR_BOTTOM_THRESHOLD
        );
    };

    useEffect(() => {
        const content = contentRef.current;
        if (!content || typeof ResizeObserver === "undefined") return undefined;

        const observer = new ResizeObserver(() => {
            if (isNearBottom()) scrollToBottom("auto");
        });
        observer.observe(content);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const currentKey = getLastMessageKey(messages);
        if (currentKey !== lastMessageKey) {
            scrollToBottom("smooth");
            setLastMessageKey(currentKey);
        }
    }, [messages, lastMessageKey]);

    // What came before the last compaction is no longer what the assistant rereads: faded.
    const lastMarkerIndex = messages.reduce(
        (last, message, index) =>
            message instanceof ConversationMarker ? index : last,
        -1,
    );
    const analysesByIndex = useMemo(
        () => analysesAtAnswerEnd(messages),
        [messages],
    );

    return (
        <ScrollArea
            className={cn("static", className)}
            scrollRef={scrollViewportRef}
            style={{ position: "static" }}
            // Radix wraps the thread in a `display: table` as wide as its widest content: one long
            // unbreakable string (a mail id, a link) widened the whole thread past the screen.
            viewportClassName="[&>div]:!block"
        >
            {/* A word too long for the line breaks instead of overflowing (inherited). */}
            <div
                ref={contentRef}
                className="mx-auto flex w-full max-w-[800px] flex-col px-5 pb-4 pt-6 [overflow-wrap:anywhere]"
            >
                {messages.map((message, index) => {
                    const isFaded = index < lastMarkerIndex;
                    // The blocks of one answer stay close; a new message breathes.
                    const isNextBlock =
                        message instanceof AIResponse &&
                        messages[index - 1] instanceof AIResponse;
                    const spacing =
                        index === 0 ? "" : isNextBlock ? "mt-3" : "mt-5";
                    if (message instanceof ConversationMarker) {
                        return (
                            <div
                                key={getMessageKey(message)}
                                className={spacing}
                            >
                                <CompactionMarker marker={message} />
                            </div>
                        );
                    }
                    // Superseded actions: not last in thread → disabled + tooltip in MessageItem.
                    const isLastMessage = index === messages.length - 1;
                    const isAIResponse = message instanceof AIResponse;
                    const hasAction =
                        isAIResponse && !!message.content.contentAction;
                    const isActionDone =
                        isAIResponse &&
                        actionDoneRequestIds.has(message.userRequestId);
                    // Only the last failed turn can be retried: later questions moved on.
                    const isRetryable =
                        isLastMessage && isAIResponse && !!message.errorCause;
                    const item = (
                        <MessageItem
                            actionDone={isActionDone}
                            chosenReply={
                                isAIResponse
                                    ? nextQuestionAfter(messages, index)
                                    : undefined
                            }
                            handleMoreDataCallBack={handleMoreDataCallBack}
                            isLastMessage={isLastMessage}
                            isLoadingMoreData={isLoadingMoreData}
                            message={message}
                            onAction={
                                isLastMessage && hasAction && isAIResponse
                                    ? () => onBulkAction(message)
                                    : undefined
                            }
                            onConfirmCreateFromSearch={
                                onConfirmCreateFromSearch
                            }
                            feedback={
                                isAIResponse && message.turnId
                                    ? answerFeedback?.[message.turnId]
                                    : undefined
                            }
                            onFeedback={onAnswerFeedback}
                            onRetry={isRetryable ? onRetry : undefined}
                            onSubmitTicket={onSubmitTicket}
                            turnAnalyses={analysesByIndex.get(index)}
                        />
                    );
                    return (
                        <div
                            key={getMessageKey(message)}
                            className={cn(
                                "w-full scroll-mt-4 transition-opacity",
                                spacing,
                                isFaded && "opacity-60",
                            )}
                            // A question: its place, for the outline at the top left of the panel.
                            {...(message instanceof UserRequest
                                ? {
                                      id: questionAnchorId(index),
                                      "data-question-index": index,
                                  }
                                : {})}
                        >
                            {isAIResponse ? (
                                <AssistantTurn withLogo={!isNextBlock}>
                                    {item}
                                </AssistantTurn>
                            ) : (
                                item
                            )}
                        </div>
                    );
                })}
                {isThinking && (
                    <div className={messages.length ? "mt-5" : ""}>
                        <AssistantTurn withLogo>
                            <ThinkingIndicator
                                reasoning={pendingReasoning ?? []}
                            />
                        </AssistantTurn>
                    </div>
                )}
            </div>
        </ScrollArea>
    );
};

// Protected: typing in the input re-renders the assistant, not its thread (audit 15/10, P4-02).
export default memo(MessageList);
